import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnalysisAdditionalInfo, AnalysisStatus } from "../../../lib/enums";
import { AnalysisStatusResponse } from "../@types";
import api from "../api";
import { isSampleAnalyzing } from "../util";
import { SAMPLE_REPORT_KEY } from "./useGetReportSummary";

const SAMPLE_ANALYSIS_REFETCH_INTERVAL = 10 * 1000;
export const ANALYSIS_STATUS_QUERY_KEY = "sampleAnalysisStatus";

export const getIsAllAnalysisFinished = (data?: AnalysisStatusResponse) => {
  if (!data) return true;
  const { tc, dynamic_analysis, ti_cloud } = data;

  const isTiCoreProcessing =
    tc.additional_information === AnalysisAdditionalInfo.PROCESSING;
  const isTiCloudProcessing =
    ti_cloud.display_status === AnalysisStatus.ANALYZING ||
    ti_cloud.display_status === AnalysisStatus.UPLOADING;
  const isDynamicAnalysisProcessing = Object.values(dynamic_analysis).some(
    item => isSampleAnalyzing(item.display_status)
  );

  return !(
    isTiCoreProcessing ||
    isTiCloudProcessing ||
    isDynamicAnalysisProcessing
  );
};

const getIsAnalysisFinished = (
  previousStatus?: string,
  currentStatus?: string
) => isSampleAnalyzing(previousStatus) && !isSampleAnalyzing(currentStatus);

const extractDisplayStatus = (data: AnalysisStatusResponse) =>
  Object.entries({
    tc: data.tc,
    ti_cloud: data.ti_cloud,
    ...data.dynamic_analysis
  }).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value.display_status
    }),
    {} as Record<string, string>
  );

const useGetSampleAnalysisStatus = (hash?: string) => {
  const queryClient = useQueryClient();
  const previousStatus =
    queryClient.getQueryData<// ResultWithPrevious<AnalysisStatusResponse>
    AnalysisStatusResponse>([ANALYSIS_STATUS_QUERY_KEY, hash ?? ""]);

  const queryFn = () => {
    if (!hash) return undefined;
    return api.getSampleAnalysisStatus(hash).then(({ data }) => {
      if (!previousStatus) return data;

      const previousStatuses = extractDisplayStatus(previousStatus);
      const currentStatuses = extractDisplayStatus(data);

      const finishedStatus = Object.entries(currentStatuses).map(
        ([key, value]) => getIsAnalysisFinished(previousStatuses[key], value)
      );

      if (finishedStatus.some(Boolean)) {
        // Delay refetching of report summary so it has time to update
        setTimeout(() => {
          queryClient.refetchQueries(
            { queryKey: [SAMPLE_REPORT_KEY] },
            { cancelRefetch: false }
          );
        }, 1000);
      }

      return data;
    });
  };

  const { data, refetch, isLoading } = useQuery({
    queryKey: [ANALYSIS_STATUS_QUERY_KEY, hash],
    queryFn,
    refetchInterval: query => {
      if (getIsAllAnalysisFinished(query.state.data)) {
        return false;
      }
      return SAMPLE_ANALYSIS_REFETCH_INTERVAL;
    }
  });

  return { data, isLoading, refetch };
};

export default useGetSampleAnalysisStatus;
